import React, { useEffect, useState, useCallback } from "react";
import OrderItemCard from "../components/Order/OrderItemCard";
import { apiGET, apiPOST, apiPUT } from "../utilities/apiHelpers";
import { API_URL } from "../config";
import { useSelector } from "react-redux";
import { FiTruck, FiEye } from "react-icons/fi";
import { HiOutlineXMark } from "react-icons/hi2";
import { MdKeyboardBackspace } from "react-icons/md";
import moment from "moment";
import scrollToTop from "../utilities/scrollToTop";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Pagination from "../components/Pagination/Pagination";
import { getImageWithFallback } from "../utilities/getImageWithFallback";

const Orders = () => {
  const userId = useSelector((state) => state.user?.userData?.id) || '';
  const navigate = useNavigate();

  const [orderData, setOrderData] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [globalConfig, setGlobalConfig] = useState([]);
  const [hoveredRow, setHoveredRow] = useState(null);

  const [modalItems, setModalItems] = useState(null);

  const [deletingId, setDeletingId] = useState(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  const handleDeleteEnquiry = (item) => {
    setSelectedEnquiry(item);
    setOpenDeleteModal(true);
  };

  const confirmDeleteEnquiry = async () => {
    if (!selectedEnquiry?._id) return;

    try {
      setDeletingId(selectedEnquiry._id);

      const response = await apiPUT(
        `/v1/order/delete-enquiry/${selectedEnquiry._id}`
      );

      // Check API response
      if (response?.data?.status) {
        toast.success("Order cancelled successfully");
        // Optionally, remove deleted order from local state
        setOrderData((prev) =>
          prev.filter((order) => order._id !== selectedEnquiry._id)
        );
      } else {
        // If API sends an error message
        toast.error(response?.data?.message || "Failed to delete enquiry order");
      }
    } catch (error) {
      console.error(error);
      // Check if error is due to waybill
      if (error?.response?.data?.message?.includes("waybill")) {
        toast.error("Cannot Cancel Order with a waybill assigned");
        // Delay the reload so the user can read the toast message
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast.error("Something went wrong");
      }
    } finally {
      setDeletingId(null);
      setOpenDeleteModal(false);
      setSelectedEnquiry(null);
    }
  };



  // Add this handler function
  const openProductModal = (e, items) => {
    e.stopPropagation(); // Prevents triggering row clicks if any
    setModalItems(items);
  };

  const getUserOrder = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const payload = {
        page,
        limit: 10,
        searchQuery: ""
      };
      const response = await apiPOST(`${API_URL}/v1/order/get-user-orders/${userId}`, payload);
      if (response?.data?.status) {
        setOrderData(response?.data?.data?.orders || []);
        setTotalPages(response?.data?.data?.totalPages || 0);
      }
    } catch (error) {
      toast.error("Failed to fetch orders");
    }
    setLoading(false);
  }, [userId, page]);

  const fetchGlobalConfig = useCallback(async () => {
    try {
      const response = await apiGET(`/v1/global-config/get-config`);
      if (response.status) {
        setGlobalConfig(response?.data?.data?.data?.[0] || {});
      }
    } catch (error) {
      toast.error('Error fetching global config');
    }
  }, []);

  const handleTrackShipment = async (waybill) => {
    if (!waybill) return toast.error("No waybill");
    try {
      const response = await apiGET(`/v1/shipment/track/${waybill}`);
      if (response.data) {
        navigate(`/order/${waybill}/order-tracking`, {
          state: { trackingData: response.data }
        });
      }
    } catch (error) {
      toast.error("Backend connection failed.");
    }
  };

  // Helper for Delivery Status Colors
  const getDeliveryStatusStyles = (status) => {
    const s = status?.toLowerCase() || '';
    if (s.includes('delivered') || s.includes('shipped')) return 'bg-green-100 text-green-700';
    if (s.includes('cancel')) return 'bg-red-100 text-red-700';
    if (s.includes('transit') || s.includes('out')) return 'bg-blue-100 text-blue-700';
    if (s.includes('pickup') || s.includes('requested')) return 'bg-orange-100 text-orange-700';
    return 'bg-gray-100 text-gray-700';
  };

  const handleSelectOrder = (order) => {
    setSelectedOrder(order); // send full order
  };


  useEffect(() => {
    getUserOrder();
    fetchGlobalConfig();
  }, [getUserOrder, fetchGlobalConfig]);

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <div className="container mx-auto p-4 min-h-screen">
      <div className="font-medium text-2xl mb-4 ">My Orders</div>

      {selectedOrder ? (
        <div>
          <button
            onClick={() => setSelectedOrder(null)}
            className="my-4 flex items-center px-4 py-2 bg-[#14967F] text-white rounded hover:bg-[#0f7a68] transition-all"
          >
            <MdKeyboardBackspace className="mr-2" /> Back to List
          </button>

          {/* Order Items */}
          <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
            {selectedOrder?.orderItems?.map((orderItem, index) => (
              <OrderItemCard
                key={index}
                orderItem={orderItem}
                order={handleSelectOrder} // send full order
                globalConfig={globalConfig}
              />
            ))}
          </div>

          <hr className="my-6 border-[3px] border-gray-200" />

          {/* Order Details */}
          <div className="bg-gray-50 border rounded-lg p-5">
            <h3 className="text-lg font-semibold text-gray-800">
              Order ID: <span className="font-mono text-sm text-[#14967F]">{selectedOrder?._id}</span>
            </h3>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Payment Details
            </h3>

            {/* Compute totals dynamically */}
            {(() => {
              // Items total: sum of orderItems discountedPrice * quantity
              const itemsTotal = selectedOrder?.orderItems?.reduce(
                (sum, item) => sum + ((item.discountedPrice ?? item.price ?? 0) * (item.quantity ?? 1)),
                0
              );

              // Packaging & Delivery charges from order or fallback to globalConfig
              const packaging_charges = selectedOrder?.packaging_charges ??  0;
              const delivery_charges = selectedOrder?.delivery_charges ??  0;

              const totalToPay = itemsTotal + packaging_charges + delivery_charges;

              return (
                <>
                  <p className="flex justify-between text-gray-600 mt-1">
                    <span>Items Total</span>
                    <span>₹{globalConfig?.currencyData?.symbol}{itemsTotal}</span>
                  </p>

                  <p className="flex justify-between text-gray-600 mt-1">
                    <span>Packaging Charges</span>
                    <span>+ ₹{globalConfig?.currencyData?.symbol}{packaging_charges}</span>
                  </p>

                  <p className="flex justify-between text-gray-600 mt-1">
                    <span>Delivery Charges</span>
                    <span>+ ₹{globalConfig?.currencyData?.symbol}{delivery_charges}</span>
                  </p>

                  <hr className="my-3" />

                  <p className="flex justify-between text-[#14967F] font-bold text-lg">
                    <span>Total to Pay</span>
                    <span>₹{globalConfig?.currencyData?.symbol}{totalToPay}</span>
                  </p>
                </>
              );
            })()}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto mt-4 rounded-lg shadow-lg">
          <div className="overflow-x-auto mt-4 rounded-xl shadow-lg border border-gray-100">
            {/* table-fixed ensures the column widths are strictly enforced */}
            <table className="min-w-full bg-white border-collapse table-fixed">
              <thead>
                <tr className="bg-[#14967F] text-white">
                  {/* Defining exact percentage widths for equal horizontal spacing */}
                  <th className="w-[12%] py-4 px-4 text-left font-bold text-xs uppercase tracking-widest">Order ID</th>
                  <th className="w-[10%] py-4 px-2 text-left font-bold text-xs uppercase tracking-widest">Mode</th>
                  <th className="w-[12%] py-4 px-2 text-left font-bold text-xs uppercase tracking-widest">Payment Type</th>
                  <th className="w-[14%] py-4 px-4 text-left font-bold text-xs uppercase tracking-widest">Payment</th>
                  <th className="w-[14%] py-4 px-4 text-left font-bold text-xs uppercase tracking-widest">Delivery</th>
                  <th className="w-[10%] py-4 px-4 text-left font-bold text-xs uppercase tracking-widest">Date</th>
                  <th className="w-[16%] py-4 px-4 text-left font-bold text-xs uppercase tracking-widest">Product</th>
                  <th className="w-[12%] py-4 px-4 text-center font-bold text-xs uppercase tracking-widest">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="py-10 text-center">
                      <div className="flex justify-center">
                        <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    </td>
                  </tr>
                ) : orderData.length ? (
                  orderData.map((item) => {
                    const isHovered = hoveredRow === item._id;
                    const isPickup = item?.orderType === 'Pickup';
                    const rawTrackStatus = item?.track?.status?.toLowerCase() || '';
                    let deliveryDisplay = item?.track?.status || "Pending";

                    if (isPickup) {
                      if (rawTrackStatus.includes('cancel')) deliveryDisplay = "Return Cancelled";
                      else if (rawTrackStatus.includes('shipped') || rawTrackStatus.includes('picked')) deliveryDisplay = "Return Shipped";
                      else deliveryDisplay = "Return Requested";
                    }

                    const firstItem = item?.orderItems?.[0];
                    const productName = firstItem?.productName || "Product";
                    const productImg = getImageWithFallback([firstItem?.productBannerImg, firstItem?.productImages?.[0]], "product");
                    const extraItems = (item?.orderItems?.length || 0) - 1;

                    return (
                      <tr
                        key={item._id}
                        onMouseEnter={() => setHoveredRow(item._id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        className="transition-all duration-200 bg-white"
                        style={{ backgroundColor: isHovered ? '#f9fafb' : 'inherit' }}
                      >
                        {/* Vertical padding reduced to py-3 to decrease Y-axis gap */}
                        <td className="py-3 px-4 font-medium text-gray-700 relative group/id align-middle">
                          <div className="flex items-center">
                            {item.reverceRequest ? (
                              <>
                                {/* Reverse order display */}
                                <span className="group-hover/id:opacity-0 transition-opacity font-mono text-sm flex items-center gap-1">
                                  {item.reverceRequest && (
                                    <span className="bg-orange-500 text-white text-[10px] px-1.5 py-0.5 rounded-md font-bold leading-none">
                                      R
                                    </span>
                                  )}
                                  <span>
                                    {item.reverceRequest ? `...${item._id.slice(-6)}` : `...${item._id.slice(-6)}`}
                                  </span>
                                </span>
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover/id:opacity-100 
                               bg-orange-500 text-white text-[10px] px-2 py-1 shadow-xl border-orange-500 border-white/20 rounded-md z-10 
                                 whitespace-nowrap transition-opacity pointer-events-none font-mono">
                                  {item._id}
                                </span>
                              </>
                            ) : (
                              <>
                                {/* Normal order display */}
                                <span className="group-hover/id:opacity-0 transition-opacity font-mono text-sm">
                                  ...{item._id.slice(-6)}
                                </span>
                                <span className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover/id:opacity-100 
                              bg-[#14967F] text-white text-[10px] px-2 py-1 shadow-xl border border-white/20 rounded-md z-10 
                                whitespace-nowrap transition-opacity pointer-events-none font-mono">
                                  {item._id}
                                </span>
                              </>
                            )}
                          </div>
                        </td>

                        <td className="py-3 px-2 text-sm text-gray-600 capitalize align-middle">

                          <div className="flex flex-col">
                            <span className="text-[11px] font-black text-gray-600 uppercase tracking-tight">
                              {item?.mode || "--"}
                            </span>
                            {isPickup && <span className="text-[9px] text-orange-500 font-bold uppercase tracking-tighter">Return</span>}
                          </div>
                        </td>

                        <td className="py-3 align-middle">
                          <div className="flex flex-col items-start gap-1.5">
                            {(() => {
                              // 1. Combine fields and normalize to lowercase for reliable checking
                              const combinedType = `${item?.orderType || ""} ${item?.mode || ""}`.toLowerCase();

                              // 2. Determine if it qualifies as Prepaid or COD
                              const isPrepaid = combinedType.includes("online") || combinedType.includes("prepaid");
                              const isCod = combinedType.includes("cod");

                              return (
                                <span
                                  className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border inline-flex items-center justify-center min-w-[65px] ${isPrepaid
                                    ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                    : isCod
                                      ? "bg-blue-50 text-blue-600 border-blue-100"
                                      : "bg-gray-50 text-gray-500 border-gray-200"
                                    }`}
                                >
                                  {isPrepaid ? "Prepaid" : isCod ? "COD" : item?.orderType || "--"}
                                </span>
                              );
                            })()}

                            {/* 3. Return Badge - Logic simplified to check for 'Pickup' */}
                            {item?.orderType === 'Pickup' && (
                              <span className="text-[9px] text-orange-600 font-extrabold uppercase tracking-widest bg-orange-50 px-2 py-0.5 rounded border border-orange-100 shadow-sm">
                                Return
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-3 px-4 align-middle">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${item?.status === 'paid' ? 'bg-green-50 text-green-700 border-green-100' : 'bg-gray-50 text-gray-500 border-gray-100'
                            }`}>
                            {item?.status || "Pending"}
                          </span>
                        </td>

                        <td className="py-3 px-4 align-middle">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${getDeliveryStatusStyles(deliveryDisplay)}`}>
                            {deliveryDisplay}
                          </span>
                        </td>

                        <td className="py-3 px-4 text-sm text-gray-500 align-middle">
                          {moment(item?.createdAt).format("DD/MM/YY")}
                        </td>

                        <td className="py-3 px-4 align-middle">
                          <div className="flex items-center gap-3 overflow-hidden">
                            <div className="h-8 w-8 flex-shrink-0 rounded-lg border overflow-hidden">
                              <img src={productImg} alt={productName} className="h-full w-full object-cover" />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <span className="text-xs font-bold text-gray-800 truncate leading-tight" title={productName}>
                                {productName}
                              </span>
                              {extraItems > 0 && (
                                <button
                                  onClick={(e) => openProductModal(e, item?.orderItems)}
                                  className="text-[9px] text-teal-600 font-bold hover:underline text-left"
                                >
                                  +{extraItems} more
                                </button>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="py-3 px-4 align-middle text-center">
                          <div className="flex justify-center gap-2">
                            {/* View Button */}
                            <div className="relative group inline-block">
                              <button
                                onClick={() => setSelectedOrder(item)}
                                className="p-2 text-blue-600 bg-blue-50 border border-blue-100 hover:border-blue-600 hover:bg-blue-600 hover:text-white rounded-lg transition-all duration-200 shadow-sm active:scale-95"
                              >
                                <FiEye size={16} className="transition-transform group-hover:scale-110" />
                              </button>

                              {/* Tooltip with Animation & Arrow */}
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-[8px] font-bold uppercase tracking-wider text-white bg-gray-900 rounded shadow-xl opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 pointer-events-none whitespace-nowrap z-20">
                                View Details
                                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                              </div>
                            </div>

                            {/* Track Shipment OR Delete */}
                            {item?.waybill ? (
                              <div className="relative group inline-block">
                                <button
                                  onClick={() => handleTrackShipment(item?.waybill)}
                                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 hover:border-emerald-600 hover:bg-emerald-600 hover:text-white rounded-lg transition-all duration-200 shadow-sm active:scale-95"
                                >
                                  <FiTruck size={14} className="group-hover:animate-bounce" />
                                  <span>Track</span>
                                </button>

                                {/* Improved Tooltip */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5 text-[8px] font-semibold uppercase tracking-wider text-white bg-gray-900 rounded shadow-xl opacity-0 group-hover:opacity-100 group-hover:-translate-y-1 transition-all duration-200 pointer-events-none whitespace-nowrap z-20">
                                  Track Order
                                  {/* Tooltip Arrow */}
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-gray-900" />
                                </div>
                              </div>
                            ) : (
                              <div className="relative group inline-block">
                                <button
                                  disabled={deletingId === item?._id}
                                  onClick={() => handleDeleteEnquiry(item)}
                                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-100 hover:border-red-600 hover:bg-red-600 hover:text-white rounded-lg transition-all duration-200 shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed group/btn"
                                >
                                  {deletingId === item?._id ? (
                                    <span className="animate-spin border-2 border-current border-t-transparent rounded-full w-3.5 h-3.5 inline-block"></span>
                                  ) : (
                                    <>
                                      <HiOutlineXMark size={14} className="group-hover/btn:scale-110 transition-transform" />
                                      <span>Cancel</span>
                                    </>
                                  )}
                                </button>

                                {/* Tooltip with Animation & Arrow */}
                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1 text-[8px] font-bold uppercase tracking-wider text-white bg-gray-900 rounded shadow-xl opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-200 pointer-events-none whitespace-nowrap z-20">
                                  Order Cancellation
                                  <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                                </div>
                              </div>
                            )}
                          </div>

                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="py-10 text-center text-gray-500 font-bold uppercase text-xs tracking-widest">
                      No Orders Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="p-4 bg-white border-t">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        </div>
      )}
      {/* Product Details Modal */}
      {/* Product Details Modal */}
      {modalItems && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[75vh] overflow-hidden flex flex-col border border-gray-200">

            {/* Modal Header */}
            <div className="p-5 border-b flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="font-bold text-xl text-gray-900">Order Items</h3>
                <p className="text-xs text-teal-600 font-bold uppercase tracking-widest">
                  {modalItems.length} {modalItems.length > 1 ? 'Products' : 'Product'}
                </p>

              </div>
              <button
                onClick={() => setModalItems(null)}
                className="px-3 py-1 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-all"
              >
                <span className="text-2xl leading-none">&times;</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="overflow-y-auto p-5 space-y-3">
              {modalItems.map((prod, idx) => {
                // Logic to handle price based on your JSON
                const displayPrice = prod?.discountedPrice || prod?.price || 0;

                return (
                  <div key={idx} className="flex items-center gap-4 p-3 rounded-xl border border-gray-100 bg-white hover:shadow-md transition-shadow">
                    <div className="w-14 h-14 flex-shrink-0">
                      <img
                        src={getImageWithFallback([prod?.productBannerImg, prod?.productImages?.[0]], "product")}
                        alt={prod?.productName}
                        className="w-full h-full object-cover rounded-lg border"
                      />
                    </div>

                    <div className="flex-1">
                      <h4 className="font-bold text-gray-800 text-sm line-clamp-1">{prod?.productName}</h4>
                      <p className="text-xs text-gray-500 font-medium">Quantity: {prod?.quantity}</p>
                      <p className="text-xs text-gray-500 font-mono">
                        <span className="text-gray-400">ID:</span> {prod?.orderId || prod?._id?.slice(-6).toUpperCase()}
                      </p>
                    </div>

                    <div className="text-right">
                      {prod?.discountedPrice < prod?.price && (
                        <div className="text-[10px] text-gray-400 line-through">
                          {globalConfig?.currencySymbol || '₹'}{prod.price}
                        </div>
                      )}
                      <div className="font-bold text-teal-600">
                        {globalConfig?.currencySymbol || '₹'}{displayPrice}
                      </div>
                      {/* <div className="text-[10px] text-gray-400 font-bold">
                                    Total: {globalConfig?.currencySymbol || '₹'}{displayPrice * prod.quantity}
                                </div> */}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-gray-50 flex flex-col gap-2">
              {/* <div className="flex justify-between items-center px-2">
                    <span className="text-sm font-medium text-gray-500">Subtotal</span>
                    <span className="text-lg font-black text-gray-900">
                        {globalConfig?.currencySymbol || '₹'}
                        {modalItems.reduce((acc, curr) => acc + ((curr.discountedPrice || curr.price || 0) * curr.quantity), 0).toLocaleString()}
                    </span>
                </div> */}
              <button
                onClick={() => setModalItems(null)}
                className="w-full py-3 bg-[#14967F] text-white font-bold rounded-xl hover:bg-[#0f7a68] transition-all shadow-lg"
              >
                Close Preview
              </button>
            </div>
          </div>

        </div>
      )}
      {/* Simple Delete Confirmation Modal */}
      {openDeleteModal && selectedEnquiry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">

            {/* Brand Header */}
            <div className="bg-[#14967F] px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white tracking-wide">
                Cancel Order
              </h3>
              <button
                onClick={() => setOpenDeleteModal(false)}
                className="text-white/80 px-2 py-2 border rounded-xl hover:text-white transition-colors hover:bg-red-400 hover:rounded-xl"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-8">
              <div className="flex flex-col items-center text-center">
                {/* Subtle Warning Icon */}
                {/* <div className="mb-4 p-3 bg-red-50 rounded-full">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </div> */}

                <p className="text-gray-600 text-base leading-relaxed">
                  Are you sure you want to cancel this order? <br />
                  <span className="text-gray-600 text-base leading-relaxed">This action cannot be undone.</span>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 mt-8">
                <button
                  onClick={confirmDeleteEnquiry}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 shadow-md shadow-red-200 active:scale-95 transition-all"
                >
                  Yes
                </button>                                                                                            
                <button
                  onClick={() => {
                    setOpenDeleteModal(false);
                    setSelectedEnquiry(null);
                  }}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 active:scale-95 transition-all"
                >
                  NO
                </button>
                
              </div>
            </div>

          </div>
        </div>
      )}

    </div>

  );
};

export default Orders;